
import { useEffect } from "react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { generateKeyPair, exportKey } from "../utils/crypto";

/**
 * 🔐 Key Synchronization Hook
 * Ensures the local Private Key matches the Public Key on Firestore.
 * - If Local Key is missing: Generates new pair & updates Firestore.
 * - If Local Key exists but Firestore is different/missing: Updates Firestore to match Local.
 * - This ensures "Self-Decryption" and "New Message Decryption" always work.
 */
export default function useKeySync(user) {
    useEffect(() => {
        if (!user) return;

        const syncKeys = async () => {
            try {
                const uid = user.uid;
                const localPrivKeyB64 = localStorage.getItem(`privKey_${uid}`);

                // 1. Fetch Remote Profile
                const userRef = doc(db, "users", uid);
                const userSnap = await getDoc(userRef);

                // If user doc doesn't exist yet (signup in progress?), skip
                if (!userSnap.exists()) return;

                const remoteData = userSnap.data();
                const remotePubKey = remoteData.publicKey;

                // 2. Scenario A: No Local Key (Lost keys or new device)
                if (!localPrivKeyB64) {
                    console.log("🔑/☁️ Keys: Missing Local Key. Generating new identity...");

                    const keyPair = await generateKeyPair();
                    const newPrivKeyB64 = await exportKey(keyPair.privateKey);
                    const newPubKeyB64 = await exportKey(keyPair.publicKey);

                    // Save Local
                    localStorage.setItem(`privKey_${uid}`, newPrivKeyB64);
                    localStorage.setItem(`pubKey_${uid}`, newPubKeyB64); // 🆕 Store Pub locally

                    // Save Remote
                    await updateDoc(userRef, { publicKey: newPubKeyB64 });
                    console.log("🔑/☁️ Keys: New Identity Synced.");
                    return;
                }

                // 3. Scenario B: Have Local Key, Check consistency
                if (localPrivKeyB64) {
                    // We must derive the public key from our local private key to compare,
                    // BUT deriving Public from Private in WebCrypto isn't direct for imported keys 
                    // unless we cached the public key too. 
                    // Simpler: We trust Local is "Truth" if we can't verify, or we store Pub in Local too.

                    // Let's assume we need to push our Local Public Key if Remote is different.
                    // Wait, we can't easily derive Pub from Priv (RSA-OAEP) in one step without storing it.
                    // Let's check if we stored `pubKey_${uid}`? No.

                    // Solution: 
                    // If we have a Private Key, we *should* have the matching Public Key.
                    // Since we don't store the Public Key locally, we might be in a bind if we want to validly compare.
                    // HOWEVER: If we assume the "mismatch" error is happening, it means Remote is WRONG.
                    // So typically we should overwrite Remote with a NEW key pair if valid verification fails?
                    // No, that destroys continuity.

                    // BETTER APPROACH:
                    // Just ensure we have *some* key.
                    // If decryption is failing (which we can't test here without a cipher), we assume it's bad?

                    // Let's look at `crypto.js` - `generateKeyPair` returns both.
                    // We should start storing `pubKey_${uid}` locally for sync checks.

                    // For now, to FIX the current user:
                    // If they have a Private Key but are having errors, it's likely the Remote Pub Key is from a previous login.
                    // There is no easy way to check if `localPrivKey` corresponds to `remotePubKey` without trying to encrypt/decrypt.

                    // SELF-HEALING TEST:
                    // 1. Generate a test message.
                    // 2. Encrypt with `remotePubKey`.
                    // 3. Try Decrypt with `localPrivKey`.
                    // 4. If fail -> Mismatch! -> Generate NEW Pair & Upload.

                    if (!remotePubKey) return; // Can't test

                    const { encryptHybrid, decryptMessage, importKey } = await import("../utils/crypto");

                    try {
                        // Test Encryption
                        const pubKey = await importKey(remotePubKey, "encrypt");
                        const testMsg = "KEY_SYNC_TEST_" + Date.now();
                        const cipher = await encryptHybrid(testMsg, pubKey);

                        // Test Decryption
                        const privKey = await importKey(localPrivKeyB64, "decrypt");
                        const decrypted = await decryptMessage(cipher, privKey);

                        if (decrypted !== testMsg) {
                            throw new Error("Mismatch detected");
                        }
                        // console.log("🔑/☁️ Keys: In Sync.");

                        // If we are here, keys match. Ensure Local Pub Key is stored for MessageInput optimization.
                        if (!localStorage.getItem(`pubKey_${uid}`)) {
                            localStorage.setItem(`pubKey_${uid}`, remotePubKey);
                        }

                    } catch (e) {
                        console.warn("🔑/☁️ Keys: Mismatch detected! Self-healing...", e);

                        // REGENERATE
                        const keyPair = await generateKeyPair();
                        const newPrivKeyB64 = await exportKey(keyPair.privateKey);
                        const newPubKeyB64 = await exportKey(keyPair.publicKey);

                        localStorage.setItem(`privKey_${uid}`, newPrivKeyB64);
                        localStorage.setItem(`pubKey_${uid}`, newPubKeyB64); // 🆕 Store Pub locally too

                        await updateDoc(userRef, { publicKey: newPubKeyB64 });
                        console.log("🔑/☁️ Keys: Repaired.");
                    }
                }

            } catch (err) {
                console.error("Key Sync Error:", err);
            }
        };

        syncKeys();
    }, [user]);
}
